import { Box, Container, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import Input from 'components/Common/Input';
import Button from 'components/Common/Button';
import { z } from 'zod';
import SecureAuthIcon from '@/assets/icons/SecureAuthIcon';
import { useLoginOnboardingForm } from '@/apis/agency/businessOnboarding/hooks';
import { setCookie } from '@/utils/helperFunctions';
import * as urls from '@/routes/config';

interface AuthFormValues {
  email: string;
  phone_number: string;
}

const authSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
  phone_number: z.string().min(1, 'Phone number is required')
});

export default function OnboardingSecureAuth() {
  const navigate = useNavigate();
  const { control, handleSubmit } = useForm<AuthFormValues>({
    resolver: zodResolver(authSchema),
    mode: 'onBlur'
  });

  const { mutate: loginOnboardingForm, isPending } = useLoginOnboardingForm();

  const onSubmit = (data: AuthFormValues) => {
    loginOnboardingForm(data, {
      onSuccess: (response) => {
        if (response?.token) {
          setCookie('onboard-token', response.form_token);
          setCookie('auth_token', response.token);
        }
        if (response?.form_token) {
          const config = response.onboarding_config;
          const stage = config?.stage;
          const step = config?.step;

          if (stage === '1') {
            navigate(`${urls.businessOnboarding.base}/${urls.businessOnboarding.stage1()}`, {
              state: { step }
            });
          } else {
            navigate(`${urls.businessOnboarding.base}/${urls.businessOnboarding.stage2()}`);
          }
        }
      },
      onError: (error: any) => {
        console.error('Login failed:', error);
      }
    });
  };

  return (
    <Container
      sx={{
        display: 'flex',
        height: { xs: '100dvh', md: '100vh' },
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5'
      }}
    >
      <Box
        sx={{
          backgroundColor: '#fff',
          borderRadius: 2,
          boxShadow: '0px 12px 30px rgba(0,0,0,0.06)',
          px: 4,
          py: 4,
          textAlign: 'center',
          width: '100%',
          maxWidth: 450
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 2
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: '#e3f2fd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <SecureAuthIcon />
          </Box>
        </Box>

        <Typography fontSize={24} fontWeight={600} mb={4} color='#000'>
          Secure Authentication
        </Typography>

        <Box component='form' onSubmit={handleSubmit(onSubmit)} display='flex' flexDirection='column' gap={2}>
          <Input
            name='email'
            label='Email *'
            placeholder='Enter Email'
            control={control}
            fullWidth
            margin='normal'
            type='email'
            sx={{
              '& .MuiInputLabel-root': {
                fontSize: '14px',
                fontWeight: 400,
                color: '#000',
                '& .MuiFormLabel-asterisk': {
                  color: '#d32f2f'
                }
              }
            }}
          />
          <Input
            name='phone_number'
            label='Password*'
            placeholder='Enter Password'
            control={control}
            fullWidth
            margin='normal'
            type='password'
            sx={{
              '& .MuiInputLabel-root': {
                fontSize: '14px',
                fontWeight: 400,
                color: '#000',
                '& .MuiFormLabel-asterisk': {
                  color: '#d32f2f'
                }
              }
            }}
          />

          <Button
            type='submit'
            variant='contained'
            fullWidth
            disabled={isPending}
            sx={{
              py: 1.5,
              fontSize: 16,
              fontWeight: 500,
              textTransform: 'none',
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0'
              }
            }}
          >
            {isPending ? 'Authenticating...' : 'Submit'}
          </Button>
        </Box>
      </Box>
    </Container>
  );
}
